import { ServerError, useMutation } from '@apollo/client';
import {
  Application,
  ApplicationContentInput,
  MutationResponse,
  UpdateApplicationInput,
} from '@bybeam/platform-types';
import useApplication from '@platform-ui-common/hooks/contexts/useApplication.js';
import useErrorIndicator from '@platform-ui-common/hooks/useErrorIndicator.js';
import MutationHookResponse from '@platform-ui/@types/mutation.js';
import UpdateApplicationMutation from '@platform-ui/application/graphql/UpdateApplication.graphql';
import { useCallback, useEffect, useMemo } from 'react';
import usePushApplicationPage from './usePushApplicationPage.js';

interface UseUpdateApplicationInput extends ApplicationContentInput {
  next?: string;
}

export default function useUpdateApplication(): MutationHookResponse<UseUpdateApplicationInput> {
  const [updateApplication, { data, loading, error: transportError }] = useMutation<
    { application: { update: MutationResponse<Application> } },
    { input: UpdateApplicationInput }
  >(UpdateApplicationMutation);

  const pushApplicationPage = usePushApplicationPage();
  const showError = useErrorIndicator();
  const application = useApplication();

  useEffect(() => {
    if ((transportError?.networkError as ServerError)?.statusCode === 413)
      showError(
        'The request size was too large. Please remove some document(s) and try again. Only 32MB can be uploaded at once.',
      );
  }, [transportError, showError]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: wants doMutation itself as a dep
  const doMutation = useCallback(
    async ({ next, ...content }: UseUpdateApplicationInput) => {
      const {
        data: {
          application: {
            update: { metadata },
          },
        },
      } = await updateApplication({
        variables: { input: { id: application.id, content } },
      });

      if (metadata.status < 400) {
        if (next) pushApplicationPage(next);
        return true;
      }

      showError('There was an error updating your application.', (): void => {
        doMutation({ next, ...content });
      });
      return false;
    },
    [updateApplication, showError, pushApplicationPage, application],
  );

  const error = transportError
    ? 'Something went wrong!'
    : data?.application.update.metadata.errors?.[0];

  return useMemo(() => ({ doMutation, loading, error }), [doMutation, loading, error]);
}
